import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { portalAuthHeaderValue } from '@/utils/tokenUtils';
import { useTranslation } from 'react-i18next';
import { Clock, CheckCircle2, FileCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live-Status der zuletzt hochgeladenen Verordnung:
 *   Eingereicht → (vom Trainer geprüft) → Freigegeben.
 * Zeigt dem Kunden transparent, dass seine Sitzungen noch geprüft werden.
 */
export const PrescriptionStatusBanner = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/portal/prescriptions/status`, { headers: { Authorization: portalAuthHeaderValue() } })
      .then((r) => { if (active && r.data?.has) setStatus(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!status || !status.has) return null;

  const pending = !!status.pending;
  const granted = status.sessions_granted || 0;

  return (
    <div
      className={`mb-4 rounded-xl border p-4 ${pending
        ? 'border-amber-500/40 bg-amber-500/10'
        : 'border-emerald-500/40 bg-emerald-500/10'}`}
      data-testid="prescription-status-banner"
    >
      <div className="flex items-start gap-3">
        {pending
          ? <Clock className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          : <FileCheck className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${pending ? 'text-amber-200' : 'text-emerald-200'}`}
             data-testid="prescription-status-title">
            {pending
              ? t('portal.rx_status.pending_title', 'Deine {{n}} Sitzungen werden geprüft', { n: granted })
              : t('portal.rx_status.released_title', '{{n}} Sitzungen freigegeben', { n: status.sessions_remaining })}
          </p>
          <p className="mt-0.5 text-xs text-white/70">
            {pending
              ? t('portal.rx_status.pending_desc', 'Dein Trainer prüft deine Verordnung. Termine kannst du schon jetzt buchen.')
              : t('portal.rx_status.released_desc', 'Deine Verordnung wurde freigegeben – du kannst deine Sitzungen buchen.')}
          </p>

          {/* Status-Stepper: Eingereicht → Freigegeben */}
          <div className="mt-3 flex items-center gap-2" data-testid="prescription-status-stepper">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <span className="text-xs font-medium text-white/80">
                {t('portal.rx_status.step_submitted', 'Eingereicht')}
              </span>
            </div>
            <div className={`h-px flex-1 ${pending ? 'bg-white/20' : 'bg-emerald-400/60'}`} />
            <div className="flex items-center gap-1.5">
              {pending
                ? <Clock className="h-4 w-4 text-amber-400" />
                : <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
              <span className={`text-xs font-medium ${pending ? 'text-white/50' : 'text-white/80'}`}
                    data-testid="prescription-status-released-label">
                {t('portal.rx_status.step_released', 'Freigegeben')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionStatusBanner;
